import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import AllocationEcho from "./AllocationEcho";
import SectionPanel from "./SectionPanel";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import Select from "@/components/Select";
import Datepicker from "@/components/Datepicker";
import Timepicker from "@/components/Timepicker";
import AppointmentsService from "@/services/appointments.service";
import { InviteFormData } from "./validation.schema";

/**
 * The participant's first appointment, recorded as part of enrolment (design screen 5a, section 2).
 *
 * SCOPE NOTE: this section is PRET-1833's deliverable, not PRET-1837's — 1833's ticket lists
 * "first-appointment section on the invite modal" in its own scope summary. It is built here at the
 * developer's direction so the enrolment journey is whole; whoever picks up PRET-1833 should not
 * build it twice.
 *
 * It is a real appointment already arranged with the treatment service; the system does not
 * generate times. The date cannot fall on the release date — enforced server-side by the same guard
 * as every other appointment, so this form does not duplicate the rule.
 *
 * Uses the repo's Datepicker and Timepicker (the same components AppointmentModal uses) so the
 * fields match every other date and time input in the back office.
 *
 * The AGENCY / LOCATION echo lives HERE, not in the assignment section above: the design shows
 * agency and location "echoed, not re-selected", as a read-only summary inside the appointment
 * section, so the coordinator can see what they are committing to.
 *
 * Note the asymmetry with the participant's own form: this Reason is a structured dropdown, while
 * the participant's is free text after the FR-18 reversal. That is deliberate in the design.
 */

interface Props {
  control: Control<InviteFormData>;
  errors: FieldErrors<InviteFormData>;
  setValue: UseFormSetValue<InviteFormData>;
  accountId?: string;
  userId?: string;
  open: boolean;
  /** Names resolved from the assignment section above, for the read-only echo. */
  agencyName?: string;
  locationName?: string;
  /** PRET-2025: the programme's specifyAppointmentReason offers the Reason dropdown. */
  showReason?: boolean;
  /** PRET-2025: the programme's specifyAppointmentReason is "mandatory". */
  reasonRequired?: boolean;
}

const FirstAppointment: React.FC<Props> = ({
  control,
  errors,
  setValue,
  accountId,
  userId,
  open,
  agencyName,
  locationName,
  showReason = true,
  reasonRequired = true,
}) => {
  const [reasons, setReasons] = useState<{ key: string; value: string }[]>([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);

  useEffect(() => {
    const loadReasons = async () => {
      if (!userId || !accountId || !open) return;
      setIsLoadingReasons(true);
      try {
        const response = await AppointmentsService.getAppointmentReasons(
          accountId,
          userId
        );
        setReasons(
          response?.flatMap(
            (reason) =>
              reason.categories?.map((category) => ({
                key: category._id,
                value: category.languages?.[0]?.name || "Reason",
              })) || []
          ) || []
        );
      } catch (error) {
        console.error("Error fetching appointment reasons", error);
        setReasons([]);
      } finally {
        setIsLoadingReasons(false);
      }
    };

    loadReasons();
  }, [userId, accountId, open]);

  const echo = useMemo(
    () => ({
      agency: agencyName ?? "Not yet selected",
      location: locationName ?? "Not yet selected",
    }),
    [agencyName, locationName]
  );

  // Typed explicitly: useWatch widens to {} here, and dayjs() will not accept that.
  const releaseDate = useWatch({ control, name: "releaseDate" }) as
    | Date
    | string
    | null
    | undefined;

  return (
    <SectionPanel
      title="First appointment"
      required
      subtitle="Record the appointment that has already been arranged for this participant. It cannot fall on the release date."
    >
      <Grid item xs={12} sm={4}>
        <Controller
          name="firstAppointmentDate"
          control={control}
          render={({ field }) => (
            <Datepicker
              {...field}
              label="Date"
              value={field.value ? dayjs(field.value as Dayjs) : null}
              onChange={(value) => field.onChange(value)}
              error={!!errors.firstAppointmentDate}
              helperText={errors.firstAppointmentDate?.message as string}
              required
              disableFuture={false}
              // Eligibility starts the DAY AFTER release, so the earliest selectable day is
              // release + 1. Greys out the impossible days rather than letting the coordinator
              // choose one and be told no on save. The schema still enforces it, because the
              // picker also accepts typed input.
              minDate={
                releaseDate ? dayjs(releaseDate).add(1, "day") : undefined
              }
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Controller
          name="firstAppointmentStart"
          control={control}
          render={({ field }) => (
            <Timepicker
              {...field}
              label="Start"
              value={field.value ? dayjs(field.value as Dayjs) : null}
              onChange={(value) => {
                field.onChange(value);
                // Same 30-minute default AppointmentModal applies, so the coordinator only has to
                // correct it when the real appointment differs.
                if (value) {
                  setValue(
                    "firstAppointmentEnd",
                    dayjs(value).add(30, "minute"),
                    { shouldValidate: true, shouldDirty: true }
                  );
                }
              }}
              error={!!errors.firstAppointmentStart}
              helperText={errors.firstAppointmentStart?.message as string}
              required
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Controller
          name="firstAppointmentEnd"
          control={control}
          render={({ field }) => (
            <Timepicker
              {...field}
              label="End"
              value={field.value ? dayjs(field.value as Dayjs) : null}
              onChange={(value) => field.onChange(value)}
              error={!!errors.firstAppointmentEnd}
              helperText={errors.firstAppointmentEnd?.message as string}
              required
            />
          )}
        />
      </Grid>

      {showReason && (
        <Grid item xs={12}>
          <Controller
            name="firstAppointmentReasonId"
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={reasons}
                value={(field.value as string) ?? ""}
                label="Reason"
                required={reasonRequired}
                id="client-first-appointment-reason"
                fullWidth
                disabled={isLoadingReasons}
                error={!!errors.firstAppointmentReasonId}
                errorMessage={
                  errors.firstAppointmentReasonId?.message as string
                }
              />
            )}
          />
        </Grid>
      )}

      {/*
        Echoed, not re-selected. Read-only summary of the assignment made above, so the coordinator
        sees where this appointment is committed. Presented as destination information rather than a
        disabled input (handoff: "Read-only is not disabled").
      */}
      <Grid item xs={12}>
        <AllocationEcho agency={echo.agency} location={echo.location} />
      </Grid>
    </SectionPanel>
  );
};

export default FirstAppointment;
